import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Settings, 
  BarChart3, 
  LogOut, 
  Menu, 
  X,
  Home,
  Tag,
  CreditCard,
  Mail,
  MapPin,
  Car,
  Calendar,
  FileText,
  Globe,
  Languages,
  Star,
  Search,
  Building2,
  MessageSquare,
  Images
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import LogoText from "@/components/LogoText";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión exitosamente",
      });
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const menuItems = [
    {
      title: "Dashboard",
      icon: LayoutDashboard,
      path: "/admin/dashboard",
    },
    {
      title: "Hero Slides",
      icon: Images,
      path: "/admin/hero-slides",
    },
    {
      title: "Contenido de Páginas",
      icon: FileText,
      path: "/admin/page-content",
    },
    {
      title: "Categorías",
      icon: Tag,
      path: "/admin/categories",
    },
    {
      title: "Blog",
      icon: FileText,
      path: "/admin/blogs",
    },
    {
      title: "Testimonios",
      icon: Star,
      path: "/admin/testimonials",
    },
    {
      title: "Tours",
      icon: MapPin,
      path: "/admin/tours",
    },
    {
      title: "Reservas Tours",
      icon: Calendar,
      path: "/admin/tour-bookings",
    },
    {
      title: "Transfers",
      icon: Car,
      path: "/admin/transfers",
    },
    {
      title: "Vehículos",
      icon: Car,
      path: "/admin/vehicle-images",
    },
    {
      title: "Reservas Transfers",
      icon: Car,
      path: "/admin/bookings",
    },
    {
      title: "Shuttle Bookings",
      icon: Calendar,
      path: "/admin/shuttle-bookings",
    },
    {
      title: "Transfer Airports",
      icon: MapPin,
      path: "/admin/transfer-airports",
    },
    {
      title: "Transfer Hotels",
      icon: Building2,
      path: "/admin/transfer-hotels",
    },
    {
      title: "Transfer Vehicles",
      icon: Car,
      path: "/admin/shuttle-vehicles",
    },
    {
      title: "Service Types",
      icon: Star,
      path: "/admin/service-types",
    },
    {
      title: "Zones & Pricing",
      icon: MapPin,
      path: "/admin/zones-pricing",
    },
    {
      title: "Promo Codes",
      icon: Tag,
      path: "/admin/promo-codes",
    },
    {
      title: "Destinos Transfers",
      icon: MapPin,
      path: "/admin/transfer-destinations",
    },
    {
      title: "Transfer Extras",
      icon: MessageSquare,
      path: "/admin/transfer-extras",
    },
    {
      title: "Métodos de Pago",
      icon: CreditCard,
      path: "/admin/payment-methods",
    },
    {
      title: "Configuración Email",
      icon: Mail,
      path: "/admin/email-settings",
    },
    {
      title: "Templates Email",
      icon: FileText,
      path: "/admin/email-templates",
    },
    {
      title: "Configuración SEO",
      icon: Search,
      path: "/admin/seo-configurations",
    },
    {
      title: "Configuración",
      icon: Settings,
      path: "/admin/settings",
    },
    {
      title: "Analíticas",
      icon: BarChart3,
      path: "/admin/analytics",
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-card border-r border-border/50 backdrop-blur-sm shadow-xl transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-border/50">
            <div className="flex items-center justify-between">
              <LogoText />
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Panel de Administración</p>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-4">
            <nav className="py-4 space-y-2">
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  variant={isActive(item.path) ? "default" : "ghost"}
                  className={`w-full justify-start h-12 ${
                    isActive(item.path) 
                      ? "bg-gradient-to-r from-primary to-purple-600 text-primary-foreground shadow-lg" 
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.title}
                </Button>
              ))}
            </nav>
          </ScrollArea>

          {/* Bottom Actions */}
          <div className="p-4 border-t border-border/50 space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start h-12 hover:bg-muted/50"
              onClick={() => {
                navigate('/');
                setSidebarOpen(false);
              }}
            >
              <Home className="mr-3 h-5 w-5" />
              Ir al Sitio Web
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="mr-3 h-5 w-5" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-72">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-sm border-b border-border/50 p-4 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;